"use client";

import { useEffect, useMemo, useState } from "react";
import SurveyCard from "./components/SurveyCard";
import { getSurveysList, getResponsesSummary } from "./helpers/surveyApi";
import Loader from "@/app/components/loader/loader";

function decodeUserIdFromToken() {
  if (typeof window === "undefined") return null;
  const token = localStorage.getItem("token");
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload?.data?.user?.id || payload?.sub || payload?.user_id || null;
  } catch {
    return null;
  }
}

export default function SurveysPage() {
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [summaries, setSummaries] = useState({});

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await getSurveysList({
          per_page: 50,
          page: 1,
          search: searchTerm || undefined,
        });
        if (cancelled) return;
        const items = res?.items || [];
        setSurveys(items);

        const userId = decodeUserIdFromToken();
        if (userId && items.length > 0) {
          try {
            const sum = await getResponsesSummary(
              items.map((s) => s.id),
              userId
            );
            if (cancelled) return;
            const map = {};
            Object.entries(sum?.summaries || {}).forEach(([k, v]) => {
              map[k] = v;
            });
            setSummaries(map);
          } catch {
            setSummaries({});
          }
        } else {
          setSummaries({});
        }
      } catch (err) {
        if (!cancelled) setError(err?.message || "تعذر تحميل الاستبيانات");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [searchTerm]);

  const mapped = useMemo(
    () =>
      surveys.map((s) => ({
        id: s.id,
        title: s.title || "بدون عنوان",
        description: s.excerpt || s.description || "",
        questions: s.totalQuestions ?? 0,
        featuredImage: s.featuredImage,
        isAnonymous: Boolean(s.isAnonymous),
        allowMultiple: Boolean(s.allowMultiple),
        status: s.status || "active",
        hasResponded: Boolean(summaries[String(s.id)]?.hasResponded),
      })),
    [surveys, summaries]
  );

  return (
    <main className="container py-4 py-md-5" dir="rtl">
      <div className="text-center mb-5">
        <h1 className="display-5 fw-bold mb-3">الاستبيانات</h1>
        <p className="lead text-muted mx-auto mb-0" style={{ maxWidth: "720px" }}>
          شاركنا تجربتك ورأيك بخطوات واضحة — بدون ضغط زمني وبدون تصحيح تلقائي. اختر استبياناً وابدأ عندما تكون جاهزاً.
        </p>
      </div>

      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
        <p className="text-muted small mb-0">
          <i className="bi bi-shield-check ms-1" aria-hidden />
          يمكن حفظ مسودة إجابتك في المتصفح أثناء التنقّل بين الأسئلة.
        </p>
        <input
          type="search"
          className="form-control rounded-pill"
          style={{ maxWidth: "280px" }}
          placeholder="ابحث عن استبيان…"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          aria-label="بحث في الاستبيانات"
        />
      </div>

      {loading && (
        <div className="text-center py-5">
          <Loader />
          <p className="mt-3 text-muted">جاري تحميل الاستبيانات…</p>
        </div>
      )}

      {error && !loading && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {!loading && !error && (
        <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-4">
          {mapped.length === 0 ? (
            <div className="col-12 text-center py-5 text-muted">لا توجد استبيانات منشورة حالياً.</div>
          ) : (
            mapped.map((s) => (
              <div key={s.id} className="col">
                <SurveyCard survey={s} />
              </div>
            ))
          )}
        </div>
      )}
    </main>
  );
}
